/**
 * Professional Login Component
 * Modern, secure login experience with forgot password functionality
 */

import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper, 
  Container,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Link,
  Divider,
  Stack
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Email as EmailIcon,
  Lock as LockIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

const Login = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login, loading, error, setError, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  // Get the redirect path from location state or default
  const from = location.state?.from?.pathname || (user?.organization?.slug ? `/org/${user.organization.slug}` : '/admin/');
  
  // Clear errors when inputs change
  useEffect(() => {
    if (error) setError(null);
    if (formError) setFormError('');
  }, [identifier, password, setError]);
  
  // If already authenticated, redirect
  useEffect(() => {
    if (user) {
      const redirectPath = user.organization?.slug ? `/org/${user.organization.slug}` : from;
      navigate(redirectPath, { replace: true });
    }
  }, [user, navigate, from]);
  
  // Handle password visibility toggle
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // Form validation
  const validateForm = () => {
    if (!identifier.trim()) {
      setFormError('Email or username is required');
      return false;
    }
    
    if (!password) {
      setFormError('Password is required');
      return false;
    }
    
    if (password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await login(identifier.trim(), password);
      if (result.success) {
        const loggedInUser = result.user;
        
        // Role-based redirect logic using the user data from login response
        const userRole = loggedInUser.user_role || loggedInUser.organization_role || loggedInUser.role;
        
        // Super Admin: Go to admin panel
        if (userRole === 'super_admin' || loggedInUser.is_admin === true) {
          const isProduction = import.meta.env.PROD;
          const adminUrl = isProduction ? '/admin/' : 'http://localhost:3000/admin/';
          window.location.href = adminUrl;
          return;
        }
        
        // Organization Admin/User: Go to organization dashboard
        if (loggedInUser.organization?.slug) {
          navigate(`/org/${loggedInUser.organization.slug}`, { replace: true });
          return;
        }
        
        // Fallback: Use original redirect logic
        const redirectPath = from && from !== '/login' ? from : '/';
        navigate(redirectPath, { replace: true });
      } else {
        setFormError(result.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      setFormError(err.message || 'Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}10 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Container component="main" maxWidth="sm">
        <Paper 
          elevation={12}
          sx={{
            padding: { xs: 3, sm: 5 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          {/* Header Section */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ mb: 2 }}>
              <img 
                src="/favico.svg" 
                alt="Situation Room Logo"
                style={{ 
                  width: 48, 
                  height: 48,
                  marginBottom: 8
                }} 
              />
            </Box>
            <Typography 
              component="h1" 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                color: theme.palette.primary.main,
                mb: 1
              }}
            >
              Situation Room
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Sign in to your dashboard
            </Typography>
          </Box>
          
          {/* Error Display */}
          {(error || formError) && (
            <Alert 
              severity="error" 
              sx={{ 
                width: '100%', 
                mb: 3,
                borderRadius: 2
              }}
            >
              {formError || error}
            </Alert>
          )}
          
          {/* Login Form */}
          <Box 
            component="form" 
            onSubmit={handleSubmit} 
            sx={{ width: '100%' }}
          >
            <Stack spacing={3}>
              <TextField
                fullWidth
                id="identifier"
                label="Email or Username"
                name="identifier"
                autoComplete="username email"
                autoFocus
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                disabled={loading || isSubmitting}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)'
                  }
                }}
              />
              
              <TextField
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading || isSubmitting}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                        disabled={loading || isSubmitting}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)'
                  }
                }}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || isSubmitting}
                sx={{ 
                  mt: 2,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  boxShadow: theme.shadows[4],
                  '&:hover': {
                    boxShadow: theme.shadows[8],
                  }
                }}
              >
                {(loading || isSubmitting) ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={20} color="inherit" />
                    <Typography>Signing in...</Typography>
                  </Stack>
                ) : (
                  'Sign In'
                )}
              </Button>
            </Stack>
          </Box>
          
          {/* Forgot Password Link */}
          <Box sx={{ mt: 3, width: '100%', textAlign: 'center' }}>
            <Link
              component={RouterLink}
              to="/forgot-password"
              variant="body2"
              sx={{
                textDecoration: 'none',
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Forgot your password?
            </Link>
          </Box>
          
          <Divider sx={{ width: '100%', my: 3 }} />
          
          {/* Additional Info */}
          <Box sx={{ textAlign: 'center' }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              <SecurityIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                Secure encrypted connection
              </Typography>
            </Stack>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Need an invitation? Contact your organization administrator.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;